import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Modal, Space, Table, Tag, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { errorText } from './helpers';

/**
 * Addresses found outside the platform and replies, from an edited Excel. Every
 * row is shown with what was wrong; only the good ones are saved.
 */
export default function ImportModal({ open, companyId, requestId, onClose, onDone, onTemplate, templateBusy }) {
  const { t } = useTranslation('partnerRequests');
  const [fileName, setFileName] = useState('');

  const preview = useMutation({
    mutationFn: (file) => API.partnerRequests.previewImport(companyId, requestId, file),
  });
  const apply = useMutation({
    mutationFn: (rows) => API.partnerRequests.applyImport(companyId, requestId, rows),
    onSuccess: (result) => {
      onDone(result);
      onClose();
    },
  });

  useEffect(() => {
    if (!open) {
      setFileName('');
      preview.reset();
      apply.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rows = preview.data?.rows || [];
  const good = rows.filter((r) => !r.errors.length);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={980}
      maskClosable={false}
      destroyOnClose
      title={t('import.title')}
      footer={
        <Space>
          <Button shape="round" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            shape="round"
            type="primary"
            disabled={!good.length}
            loading={apply.isPending}
            onClick={() => apply.mutate(good)}
          >
            {t('import.submit', { count: good.length })}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" className="w-full">
        <p className="text-sm opacity-70">{t('import.help')}</p>
        <Space wrap>
          <Upload
            accept=".xlsx"
            showUploadList={false}
            beforeUpload={(file) => {
              setFileName(file.name);
              preview.mutate(file);
              return false;
            }}
          >
            <Button shape="round" icon={<InboxOutlined />} loading={preview.isPending}>
              {t('import.pick')}
            </Button>
          </Upload>
          <Button shape="round" icon={<DownloadOutlined />} loading={templateBusy} onClick={onTemplate}>
            {t('import.template')}
          </Button>
          {fileName && <span className="text-sm">{fileName}</span>}
        </Space>
        {(preview.isError || apply.isError) && (
          <Alert type="error" showIcon message={errorText(t, preview.error || apply.error)} />
        )}
        {preview.data && (
          <>
            <Alert
              type={good.length === rows.length ? 'success' : 'warning'}
              showIcon
              message={t('import.summary', { good: good.length, bad: rows.length - good.length })}
            />
            <Table
              size="small"
              rowKey="row"
              dataSource={rows}
              pagination={{ pageSize: 50, hideOnSinglePage: true }}
              columns={[
                { title: t('import.row'), dataIndex: 'row', width: 60, align: 'right' },
                { title: t('columns.name'), dataIndex: 'name', ellipsis: true },
                { title: t('columns.emails'), dataIndex: 'emails', render: (v) => v.join('; ') || '—' },
                {
                  title: t('columns.reply'),
                  dataIndex: 'reply',
                  width: 170,
                  render: (v) => (v ? t(`replies.${v}`) : '—'),
                },
                {
                  title: t('columns.status'),
                  width: 200,
                  render: (r) =>
                    r.errors.length ? (
                      r.errors.map((e) => (
                        <Tag key={e} color="red">
                          {t(`import.errors.${e}`)}
                        </Tag>
                      ))
                    ) : (
                      <Tag color="green">{t('import.ok')}</Tag>
                    ),
                },
              ]}
            />
          </>
        )}
      </Space>
    </Modal>
  );
}
